import { useNavigate } from "react-router-dom";
import { API_CORE } from "../../../../API/ApiCORE";
import { APP_STORAGE } from "../../../../_Config/AppConfig.Storage";
import { useReactNavigation } from "../../Hooks/useReactNavigation";
import { CONFIG_PLUGIN } from "../../../../_Config/Plugins/_Config.Plugin";
import { STORAGE_SESSION_HELPER } from "../../../../HELPERS/storageSession.helper";
import { AUTH_CONSTANTS } from "../../../../../Web/Views/Public/_Auth/_MODULES/_API";
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const UserAuthContext = createContext();

export const useUserAuthenticated = () => {
    const context = useContext(UserAuthContext);

    if (context === undefined) {
        return {
            useDataUserAuth: {
                isLoading: false, isAuthenticated: false, token: null, data: null,
            },
            onCallbackCredentialUserAuth: () => { },
            onCallbackCloseSessionUserAuth: () => { },
            refreshUserAuth: async () => null
        };
    }

    return context;
};

export const UserAuthenticatedProvider = ({ children }) => {

    const { redirectTo } = useReactNavigation();

    const [useDataUserAuth, setDataUserAuth] = useState({
        isLoading: true, isAuthenticated: false, token: null, data: null,
        isServerDown: false,
    });

    // ----------------------------- 🔥 Validar token mediante la sesión activa
    const LOAD_GET_USER_FROM_TOKEN = async (TOKEN_USER_AUTH) => {
        try {
            if (!TOKEN_USER_AUTH || typeof TOKEN_USER_AUTH !== 'string') {
                throw new Error("Token inválido o inexistente");
            }

            const RESPONSE = await API_CORE.SERVICES.GET_RESOURCE(API_CORE.ENDPOINTS[AUTH_CONSTANTS.ME]);

            // 🔥 server caído / sin respuesta
            const STATUS = RESPONSE?.status ?? RESPONSE?.code_status;
            const IS_SERVER_DOWN = !RESPONSE || STATUS >= 500 || RESPONSE?.isOffline;

            if (IS_SERVER_DOWN) {
                setDataUserAuth({
                    isLoading: false, isAuthenticated: false,
                    token: TOKEN_USER_AUTH, data: null,
                    isServerDown: true,
                });
                return null;
            }

            if (RESPONSE.success && RESPONSE.data) {
                setDataUserAuth({
                    isLoading: false, isAuthenticated: true,
                    token: TOKEN_USER_AUTH, data: RESPONSE.data,
                    isServerDown: false,
                });
                return RESPONSE.data;
            }

            // 401 / token inválido → NO es server down
            setDataUserAuth({
                isLoading: false, isAuthenticated: false,
                token: null, data: null, isServerDown: false,
            });
            return null;

        } catch (error) {
            const HAS_RESPONSE = !!error?.response;
            const STATUS = error?.response?.status;
            const IS_SERVER_DOWN = !HAS_RESPONSE || STATUS >= 500;

            setDataUserAuth({
                isLoading: false, isAuthenticated: false,
                token: IS_SERVER_DOWN ? TOKEN_USER_AUTH : null,
                data: null,
                isServerDown: IS_SERVER_DOWN,
            });
            return null;
        }
    };

    // ----------------------------- 🔥 Auto login al arrancar la aplicación 
    useEffect(() => {
        let isMounted = true;

        const initAuth = async () => {
            try {
                const TOKEN = STORAGE_SESSION_HELPER.GET_STORAGE(APP_STORAGE.TOKEN_USER_AUTH);

                if (!TOKEN || !isMounted) {
                    setDataUserAuth(prev => ({ ...prev, isLoading: false }));
                    return;
                }

                await LOAD_GET_USER_FROM_TOKEN(TOKEN);

            } catch (err) {
                if (isMounted) {
                    setDataUserAuth({
                        isLoading: false, isAuthenticated: false,
                        token: null, data: null
                    });
                }
            }
        };

        initAuth();
        return () => { isMounted = false; };
    }, []);

    // ----------------------------- 🔥 LOGIN - Solo procesa el accessToken
    const CB__onCallbackCredentialUserAuth = useCallback(async ({ accessToken }) => {

        if (!accessToken) return;

        try {
            STORAGE_SESSION_HELPER.SET_STORAGE(APP_STORAGE.TOKEN_USER_AUTH, accessToken);

            const USER_DATA = await LOAD_GET_USER_FROM_TOKEN(accessToken);

            STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_VERIFY_EMAIL);
            STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_CHALLENGE);
            STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_EMAIL_MASKED);
            STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_EXPIRES);

            if (USER_DATA) {
                if (typeof window !== "undefined") {
                    redirectTo("/dashboard");
                }
            }
        } catch (e) {
            // console.error("Error en onCallbackCredentialUserAuth:", e);
        }
    }, []);

    // ----------------------------- 🔥 Refrescar datos del usuario bajo demanda
    const CB__refreshUserAuth = useCallback(async () => {
        const TOKEN = STORAGE_SESSION_HELPER.GET_STORAGE(APP_STORAGE.TOKEN_USER_AUTH);
        if (!TOKEN) return null;
        return await LOAD_GET_USER_FROM_TOKEN(TOKEN);
    }, []);

    // ----------------------------- 🔥 Cierre de sesión y limpieza de memoria
    const CB__onCallbackCloseSessionUserAuth = useCallback(() => {
        try {
            STORAGE_SESSION_HELPER.CLEAR_AUTH();
            STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_VERIFY_EMAIL);
            STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_CHALLENGE);
            STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_EMAIL_MASKED);
            STORAGE_SESSION_HELPER.REMOVE_STORAGE(APP_STORAGE.AUTH_2FA_EXPIRES);
        } catch (e) {
            // console.error("Error al cerrar sesión:", e);
        } finally {
            setDataUserAuth({
                isLoading: false, isAuthenticated: false,
                token: null, data: null
            });

            if (typeof window !== "undefined") {
                redirectTo("/");
            }
        }
    }, []);


    const CB__retryServerConnection = useCallback(async () => {
        const TOKEN = STORAGE_SESSION_HELPER.GET_STORAGE(APP_STORAGE.TOKEN_USER_AUTH);
        setDataUserAuth(prev => ({ ...prev, isLoading: true }));
        if (!TOKEN) {
            setDataUserAuth({ isLoading: false, isAuthenticated: false, token: null, data: null, isServerDown: false });
            return null;
        }
        return await LOAD_GET_USER_FROM_TOKEN(TOKEN);
    }, []);

    return (
        <UserAuthContext.Provider value={{
            useDataUserAuth, onCallbackCredentialUserAuth: CB__onCallbackCredentialUserAuth, onCallbackCloseSessionUserAuth: CB__onCallbackCloseSessionUserAuth, refreshUserAuth: CB__refreshUserAuth,
            retryServerConnection: CB__retryServerConnection, setDataUserAuth,
        }}>
            {children}
        </UserAuthContext.Provider>
    );
};